/** Minimal pub/sub for routing terminal output frames to the active terminal. */
import { terminalReplayChunk } from './terminal-replay.js';

type Listener = (bytes: Uint8Array) => void;

const MAX_BUFFER_BYTES = 512 * 1024;

interface OutputChunk {
  seq: number;
  bytes: Uint8Array;
}

export class OutputBus {
  private readonly listeners = new Map<string, Set<Listener>>();
  private readonly buffers = new Map<string, { chunks: OutputChunk[]; bytes: number }>();
  private nextSeq = 1;

  subscribe(sessionId: string, listener: Listener): () => void {
    let set = this.listeners.get(sessionId);
    if (!set) {
      set = new Set();
      this.listeners.set(sessionId, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
      if (set && set.size === 0) this.listeners.delete(sessionId);
    };
  }

  emit(sessionId: string, bytes: Uint8Array): void {
    this.append(sessionId, bytes);
    const set = this.listeners.get(sessionId);
    if (!set) return;
    for (const listener of set) listener(bytes);
  }

  replay(sessionId: string, listener: Listener): void {
    const buffer = this.buffers.get(sessionId);
    if (!buffer) return;
    for (const chunk of buffer.chunks) listener(chunk.bytes);
  }

  private append(sessionId: string, bytes: Uint8Array): void {
    let buffer = this.buffers.get(sessionId);
    if (!buffer) {
      buffer = { chunks: [], bytes: 0 };
      this.buffers.set(sessionId, buffer);
    }

    const replay = terminalReplayChunk(bytes);
    if (replay.resets) {
      buffer.chunks = [];
      buffer.bytes = 0;
    }

    buffer.chunks.push({ seq: this.nextSeq++, bytes: replay.bytes });
    buffer.bytes += replay.bytes.byteLength;

    while (buffer.bytes > MAX_BUFFER_BYTES && buffer.chunks.length > 1) {
      const removed = buffer.chunks.shift();
      if (removed) buffer.bytes -= removed.bytes.byteLength;
    }
  }
}
